import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

/**
 * Reusable confirmation modal — replaces native `window.confirm`/`prompt`,
 * which mobile browsers + some incognito sessions can silently block and
 * which never matches our visual language.
 *
 * Props:
 *   modal: null | {
 *     title: string,
 *     body: React.ReactNode,
 *     destructive?: boolean,
 *     confirmLabel?: string,
 *     requirePhrase?: string,   // user must type this verbatim to confirm
 *     onConfirm: () => Promise<void> | void,
 *   }
 *   onClose: () => void
 *
 * Usage:
 *   const [confirmModal, setConfirmModal] = useState(null);
 *   setConfirmModal({ title, body, destructive, confirmLabel, onConfirm })
 *   <ConfirmModal modal={confirmModal} onClose={() => setConfirmModal(null)} />
 */
export default function ConfirmModal({ modal, onClose }) {
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset whenever the modal payload changes (or closes/reopens).
  useEffect(() => { setPhrase(""); setBusy(false); }, [modal]);

  if (!modal) return null;
  const needsPhrase = !!modal.requirePhrase;
  const canConfirm = !needsPhrase || phrase === modal.requirePhrase;

  const runConfirm = async () => {
    if (!canConfirm || busy) return;
    setBusy(true);
    try {
      await modal.onConfirm?.();
    } finally {
      setBusy(false);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm animate-in fade-in"
      data-testid="confirm-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="bg-white border border-zinc-200 w-full max-w-md shadow-2xl">
        <div className="px-6 py-5 border-b border-zinc-200 flex items-start gap-3">
          {modal.destructive && (
            <div className="w-9 h-9 bg-red-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
          )}
          <div>
            <h3 className="font-heading text-lg font-bold" data-testid="confirm-title">{modal.title}</h3>
            <div className="text-sm text-zinc-600 mt-1" data-testid="confirm-body">{modal.body}</div>
          </div>
        </div>
        {needsPhrase && (
          <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50">
            <label className="overline block mb-2">
              Type <span className="font-mono bg-white border border-zinc-300 px-1.5 py-0.5">{modal.requirePhrase}</span> to confirm
            </label>
            <input
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              data-testid="confirm-phrase-input"
              autoFocus
              className="w-full px-3 py-2 text-sm font-mono border border-zinc-300 focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
            />
          </div>
        )}
        <div className="px-6 py-4 flex items-center justify-end gap-2 bg-zinc-50">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="confirm-cancel"
            className="px-4 py-2 text-xs font-bold uppercase tracking-widest text-zinc-700 hover:bg-zinc-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={runConfirm}
            disabled={!canConfirm || busy}
            data-testid="confirm-ok"
            className={`px-5 py-2 text-xs font-bold uppercase tracking-widest text-white flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition ${
              modal.destructive
                ? "bg-red-600 hover:bg-red-700"
                : "bg-[#002FA7] hover:bg-[#00227A]"
            }`}
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {modal.confirmLabel || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
